<template>

  <div :style="`height: ${mapHeight}px; width: 100%`">
    <l-map class="" v-if="showMap" :zoom="zoom" :center="center" :options="mapOptions" style="height: 100%">
      <l-tile-layer :url="url" :attribution="attribution"/>
        <l-marker v-for="(marker, markerIndex) in  markers" v-bind:key="markerIndex+1" 
                  :lat-lng="marker.coords" 
                  :icon="createIcon(marker)">
            <l-popup>
                <p class="fw-light fw-bold fs-5 w-100" style="margin-bottom: -5%; text-transform: uppercase;">{{marker.name}}</p>
                <input type="text" class="bg-transparent border-0 fs-6 w-100 mt-3" style="margin-bottom: -50px;" :value="marker.alias" readonly>
                <p class="fw-light fw-bold fs-6 w-100" style="margin-bottom: -5%; margin-top: 2%;">IP: {{marker.ip}}</p>
                <p class="fw-light fs-6 w-100" style="margin-bottom: 3%;">Device Role: {{marker.role}}</p>
                <ul>
                  <li v-for="(message, messageIndex) in marker.messages" v-bind:key="messageIndex+1">
                    {{message}}
                  </li>
                </ul>
                <button class="btn fw-light fs-6 mt-1 w-100"  :class="[marker.connectivity.classIndicator]" title="Connectivity">
                    <i class="fa-solid fa-xl fa-tower-broadcast"></i>
                </button>
                <button class="btn fs-6 mt-1" style="width: 33%" :class="[marker.ping.classIndicator]" :title="`Ping | Success: ${marker.ping.success}%, Fail: ${marker.ping.failed}%, Fail Tolerance: ${marker.ping.tolerance}%`">
                    <i class="fa-solid fa-xl fa-rss me-2"></i>{{marker.ping.success}}%
                </button>
                <button class="btn fs-6 mt-1 me-1 ms-1" style="width: 32%" :class="[marker.cpu.classIndicator]" :title="`CPU Usage | Value: ${marker.cpu.value}%, Tolerance: ${marker.cpu.tolerance}%`">
                    <i class="fa-solid fa-xl fa-microchip me-2"></i>{{marker.cpu.value}}%
                </button>
                <button class="btn fs-6 mt-1" style="width: 32%" :class="[marker.memory.classIndicator]" :title="`Memory Usage | Value: ${marker.memory.value}%, Tolerance: ${marker.memory.tolerance}%`">
                    <i class="fa-solid fa-xl fa-memory me-2"></i>{{marker.memory.value}}%
                </button>
                <div class="w-100 rounded p-1 mt-1" :class="{'bg-secondary': marker.targets.exec_targets, 'bg-dark': !marker.targets.exec_targets}" title="Total Targets">
                    <button class="btn fs-6 mt-1 w-100" :class="{'btn-secondary': marker.targets.exec_targets, 'btn-dark': !marker.targets.exec_targets}" title="Up Targets" style="width: 40%">
                      <i class="fa-solid fa-xl fa-bullseye me-2"></i>
                      {{marker.targets.up}}
                    </button>
                    <div class="input-group flex-nowrap">
                      <button class="btn fs-6 mt-1 me-1 w-50 rounded" :class="{'btn-success': marker.targets.exec_targets, 'btn-dark': !marker.targets.exec_targets}" title="Up Targets">
                          <i class="fa-solid fa-xl fa-check me-2"></i>
                          {{marker.targets.up}}
                      </button>
                      <button class="btn fs-6 mt-1 ms-1 w-50 rounded" :class="{'btn-danger': marker.targets.exec_targets, 'btn-dark': !marker.targets.exec_targets}" title="Not Up Targets">
                          <i class="fa-solid fa-xl fa-exclamation me-2"></i>
                          {{marker.targets.notup}}
                      </button>
                    </div>
                </div>
            </l-popup>
        </l-marker>
    </l-map>
    <div v-if="showTypeControls">
      <div class="input-group flex-nowrap">
        <button class="btn btn-success fw-light fs-5 w-100" @click="setMapTile('terrain')">
            <i class="fa-solid fa-xl fa-layer-group me-2"></i>Terrain
        </button>
        <button class="btn btn-info fw-light fs-5 w-100 text-white" @click="setMapTile('general')">
            <i class="fa-solid fa-xl fa-layer-group me-2"></i>General
        </button>
        <button class="btn btn-dark fw-light fs-5 w-100" @click="setMapTile('toner')" >
            <i class="fa-solid fa-xl fa-layer-group me-2"></i>Black & White
        </button>
        <button class="btn btn-primary fw-light fs-5 w-100" @click="setMapTile('watercolor')">
            <i class="fa-solid fa-xl fa-layer-group me-2"></i>Watercolor
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { latLng, icon, Path } from "leaflet";
import { LMap, LTileLayer, LMarker, LPopup, LTooltip} from "vue2-leaflet";

// My Icons

export default {
  name: "MapViewer",
  props: {
    mapHeight: String,
    icons: Object,
    markers: Array,
    centerLatitude: Number,
    centerLongitude: Number,
    zoom: Number,
    mapKey: String
  },
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LTooltip
  },
  data() {
    return {
        showTypeControls: false,
        mapTile: 'terrain',
        url: 'https://stamen-tiles-{s}.a.ssl.fastly.net/terrain/{z}/{x}/{y}.jpg',
        attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        mapOptions: {
            zoomSnap: 0.25
        },
        showMap: true,
        tiles: {
            terrain: 'https://stamen-tiles-{s}.a.ssl.fastly.net/terrain/{z}/{x}/{y}.jpg',
            dark: 'https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png',
            toner: 'https://stamen-tiles-{s}.a.ssl.fastly.net/toner/{z}/{x}/{y}.png',
            general: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            watercolor: 'https://stamen-tiles-{s}.a.ssl.fastly.net/watercolor/{z}/{x}/{y}.jpg',
        }
    };
  },
  methods: {
    setMapTile(mapTile){
        this.mapTile = mapTile
        this.url = this.tiles[this.mapTile] + '?api_key=' + String(this.mapKey)
    },
    createIcon(iconData){
        return icon({
            iconUrl: this.icons[iconData.icon].path,
            iconSize: this.icons[iconData.icon].size,
            iconAnchor: this.icons[iconData.icon].anchor
        })
    }
  },
  computed: {
    center(){
      return latLng(this.centerLatitude, this.centerLongitude)
    }
  },
  mounted(){
    this.setMapTile('dark')
  }
};
</script>
